/**
 * Tool UI for batch_screenshot — a single tool call that renders many
 * screenshots (locales / viewports / pages). Shows:
 *  - Live per-shot progress while running (n/total + current label), driven by
 *    the `batch-progress` SSE channel.
 *  - A per-shot result list on completion with ✓ / ✗ and the saved path or error,
 *    plus a succeeded/failed tally so failures are obvious at a glance.
 */
import { useState, useEffect } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import { useBatchProgressStream } from '../useShellOutputStream';
import { Images, XCircle, Check, Circle } from 'lucide-react';
import { GeneratedFiles } from './GeneratedFiles';
import { useOffloadedResult } from './useOffloadedResult';
import { ToolRow, Shimmer, DetailPanel, DetailPre, plainText, wasInterrupted, INTERRUPTED_META } from '../toolRow';

interface ShotArg {
  directory: string;
  saveToWorkspace: string;
  locale?: string;
  label?: string;
}
interface BatchArgs {
  shots?: ShotArg[];
  perShotTimeoutMs?: number;
  _thought?: string;
}
interface ShotResult {
  index: number;
  label?: string;
  directory?: string;
  locale?: string;
  savedPath?: string;
  resolution?: string;
  jsErrors?: string[];
  error?: string;
}
interface BatchResult {
  status?: 'succeeded' | 'partial' | 'failed';
  total?: number;
  succeeded?: number;
  failed?: number;
  shots?: ShotResult[];
  durationMs?: number;
  error?: string;
}

export const BatchScreenshotToolUI = makeAssistantToolUI<BatchArgs, BatchResult>({
  toolName: 'batch_screenshot',
  render: function BatchScreenshotRender(props: any) {
    const { args, status } = props;
    // Each job's row is long enough that a batch of any size comes back offloaded.
    const offloaded = useOffloadedResult<BatchResult>(props.result);
    const result: BatchResult | undefined = offloaded.data ?? props.result;
    const isRunning = status.type === 'running';
    const [expanded, setExpanded] = useState(true);

    const live = useBatchProgressStream('batch_screenshot');
    const total = result?.total ?? args?.shots?.length ?? live.find((e) => e.total)?.total ?? 0;

    // Auto-collapse once finished and everything passed.
    useEffect(() => {
      if (!isRunning && result && !result.failed) setExpanded(false);
    }, [isRunning, result?.failed]);

    const failed = (result?.failed ?? 0) > 0 || !!result?.error;
    const succeeded = !!result && !failed;

    const liveDoneCount = live.filter((e) => e.status !== 'rendering').length;
    const liveCurrent = [...live].reverse().find((e) => e.status === 'rendering');

    return (
      <>
      <ToolRow
        icon={!isRunning && failed ? <XCircle className="text-red-500" /> : <Images />}
        label={plainText(args?._thought) || `Batch screenshot${total ? ` · ${total} shots` : ''}`}
        running={isRunning}
        tone={!isRunning && failed ? 'error' : 'default'}
        meta={
          isRunning ? `${total ? `${liveDoneCount}/${total}` : ''}${liveCurrent?.label ? ` · ${liveCurrent.label}` : ''}` || undefined
            : succeeded ? `${result!.succeeded}/${result!.total}`
            : failed ? <span className="text-red-600">{result?.failed ?? '!'} failed</span>
            : wasInterrupted(status, result) ? INTERRUPTED_META
            : undefined
        }
        open={expanded}
        onToggle={() => setExpanded((e) => !e)}
      >
          <div className="space-y-1">
            {/* Live rows while running */}
            {isRunning && live.length > 0 && live.map((e) => (
              <ProgressRow
                key={e.index}
                label={e.label || e.locale || `shot ${e.index + 1}`}
                status={e.status}
                detail={e.status === 'error' ? e.error : e.savedPath}
              />
            ))}
            {isRunning && live.length === 0 && (
              <div className="text-xs text-zinc-400">Starting renders…</div>
            )}

            {/* Final per-shot results */}
            {!isRunning && result?.shots?.map((s: ShotResult) => (
              <ProgressRow
                key={s.index}
                label={s.label || s.locale || `shot ${s.index + 1}`}
                status={s.error ? 'error' : 'done'}
                detail={s.error ? s.error : `${s.savedPath}${s.resolution ? ` · ${s.resolution}` : ''}`}
                warn={s.jsErrors && s.jsErrors.length > 0 ? `${s.jsErrors.length} JS error(s)` : undefined}
              />
            ))}

            {!isRunning && result?.error && (
              <DetailPanel label="Error" tone="error"><DetailPre tone="error">{result.error}</DetailPre></DetailPanel>
            )}
            {!isRunning && result?.durationMs != null && (
              <div className="text-xs text-zinc-400">{Math.round(result.durationMs / 100) / 10}s total</div>
            )}
          </div>
      </ToolRow>
      {!isRunning && <GeneratedFiles result={result} />}
      </>
    );
  },
});

function ProgressRow({
  label, status, detail, warn,
}: { label: string; status: 'preparing' | 'rendering' | 'done' | 'error'; detail?: string; warn?: string }) {
  return (
    <div className="flex items-center gap-2 text-meta">
      {status === 'rendering' || status === 'preparing' ? (
        <Circle className="w-3 h-3 text-zinc-300 shrink-0" />
      ) : status === 'error' ? (
        <XCircle className="w-3 h-3 text-red-500 shrink-0" />
      ) : (
        <Check className="w-3 h-3 text-zinc-400 shrink-0" />
      )}
      <span className="text-zinc-700 shrink-0">{status === 'rendering' || status === 'preparing' ? <Shimmer>{label}</Shimmer> : label}</span>
      {detail && (
        <span className={`font-mono text-xs truncate ${status === 'error' ? 'text-red-600' : 'text-zinc-400'}`}>{detail}</span>
      )}
      {warn && <span className="text-xs text-yellow-600 shrink-0">{warn}</span>}
    </div>
  );
}
